import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { studyGroupMembershipApi } from "@/lib/humhub-api/studyGroupMembership";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

type UseStudyGroupMembershipsOptions = {
  enabled?: boolean;
  page?: number;
  keyword?: string;
  onPageChange?: (page: number) => void;
  onKeywordChange?: (keyword?: string) => void;
};

export const useStudyGroupMemberships = (
  studyGroupId: string | number | null,
  perPage = 20,
  initialPage = 1,
  keyword?: string,
  membershipStatus?: number,
  options?: UseStudyGroupMembershipsOptions,
) => {
  const [internalPage, setInternalPage] = useState<number>(initialPage);
  const [internalSearchKeyword, setInternalSearchKeyword] = useState<
    string | undefined
  >(keyword);
  const page = options?.page ?? internalPage;
  const searchKeyword = options?.keyword ?? internalSearchKeyword;
  const enabled = (options?.enabled ?? true) && !!studyGroupId;

  const setPage = useCallback(
    (newPage: number) => {
      if (options?.onPageChange) {
        options.onPageChange(newPage);
      }
      if (options?.page === undefined) {
        setInternalPage(newPage);
      }
    },
    [options],
  );

  const setKeyword = useCallback(
    (newKeyword?: string) => {
      if (options?.onKeywordChange) {
        options.onKeywordChange(newKeyword);
      }
      if (options?.keyword === undefined) {
        setInternalSearchKeyword(newKeyword);
      }
    },
    [options],
  );

  // React Queryでメンバーシップ取得
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: studyGroupKeys.memberships({
      studyGroupId,
      page,
      perPage,
      keyword: searchKeyword,
      membershipStatus,
    }),
    queryFn: () => {
      if (!studyGroupId) {
        throw new Error("Study group ID is null");
      }
      return studyGroupMembershipApi.getStudyGroupMemberships(
        studyGroupId,
        perPage,
        page,
        searchKeyword,
        membershipStatus,
      );
    },
    enabled,
    staleTime: 3 * 60 * 1000, // メンバーシップは3分間キャッシュ
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // ページ取得関数（既存インターフェース維持）
  const fetchPage = useCallback(
    async (newPage = 1, newSearchKeyword?: string) => {
      setPage(newPage);
      if (newSearchKeyword !== undefined) {
        setKeyword(newSearchKeyword);
      }
      // setPageとsetSearchKeywordが完了すると、useQueryのqueryKeyが変わり自動で新しいクエリが実行される
    },
    [setKeyword, setPage],
  );

  // 検索機能
  const searchMembers = useCallback(
    (newKeyword?: string) => {
      setKeyword(newKeyword);
      setPage(1); // 検索時はページを1に戻す
    },
    [setKeyword, setPage],
  );

  // ページ変更
  const changePage = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  return {
    data: data?.results || [],
    pagination: data
      ? {
          total: data.total,
          page: data.page,
          pages: data.pages,
          perPage,
        }
      : undefined,
    isLoading,
    error: queryError ? String(queryError) : null,
    fetchPage, // 既存インターフェース維持
    searchMembers, // 新しく追加：検索機能
    changePage, // 新しく追加：ページ変更
    refetch: () => refetch(), // 手動リフェッチ
    currentKeyword: searchKeyword, // 新しく追加：現在の検索キーワード
    currentPage: page, // 新しく追加：現在のページ
  };
};
